import { useCallback, useEffect, useState } from 'react';
import { fechaHoraNegocio } from '@tanteo/compartido';
import {
  auditoriaPlataforma,
  tiposEventoPlataforma,
  type EventoAuditoria,
} from '../../lib/api-panel';
import { Boton, Campo, CampoLista, EncabezadoTabla, Esqueleto, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, TextoAyuda } from '../../ui/base';

const POR_PAGINA = 50;

/** Nombre legible de cada tipo; los que no estén se muestran crudos. */
const ETIQUETAS: Record<string, string> = {
  negocio_creado: 'Negocio creado',
  venta_anulada: 'Comprobante anulado',
  gasto_editado: 'Gasto corregido',
  gasto_anulado: 'Gasto anulado',
  venta_con_descuento: 'Venta con descuento',
  descuento_automatico_quitado: 'Descuento automático quitado',
  descuento_editado: 'Descuento editado',
  caja_abierta: 'Caja abierta',
  base_caja_corregida: 'Base de caja corregida',
  cuadre_cerrado: 'Caja cerrada',
  tercero_suprimido: 'Datos de un tercero suprimidos',
  producto_suprimido: 'Producto eliminado',
  tercero_registrado: 'Tercero registrado',
  invitacion_creada: 'Invitación enviada',
  invitacion_aceptada: 'Invitación aceptada',
  invitacion_anulada: 'Invitación anulada',
  sesion_desconectada: 'Sesión desconectada',
  cuadre_reabierto: 'Día reabierto',
  caja_apertura_descartada: 'Apertura de caja descartada',
  catalogo_importado: 'Catálogo importado',
  catalogo_editado: 'Catálogo editado',
  cuenta_editada: 'Cuenta de pago editada',
  impuesto_editado: 'Impuesto editado',
  insumo_editado: 'Insumo editado',
  merma_registrada: 'Merma de inventario registrada',
  receta_guardada: 'Receta guardada',
  bot_codigo_generado: 'Código del bot generado',
  bot_vinculo_fallido: 'Código del bot equivocado',
  miembro_renombrado: 'Miembro renombrado',
  error_cliente: 'Error reportado por la aplicación',
  respaldo_ok: 'Respaldo diario completado',
  cuenta_huerfana_borrada: 'Cuenta a medias borrada',
  sonda_ok: 'Sonda del canario: en orden',
  sonda_fallo: 'Sonda del canario: falló',
  alerta_entregada: 'Alerta entregada',
  alerta_no_entregada: 'Alerta que no salió por ningún canal',
  vigilancia_rls_ausente: 'Vigilancia: tabla sin RLS',
  vigilancia_dia_sin_cuadre: 'Vigilancia: día sin cuadre',
  vigilancia_consecutivo_drift: 'Vigilancia: hueco en la numeración de comprobantes',
  vigilancia_capacidad: 'Vigilancia: la base se acerca a su capacidad',
  vigilancia_existencia_negativa: 'Vigilancia: insumo en negativo',
  vigilancia_cuenta_huerfana: 'Vigilancia: cuenta sin negocio',
  vigilancia_venta_sin_lineas: 'Vigilancia: venta sin líneas',
  vigilancia_venta_tras_cierre: 'Vigilancia: venta después del cierre del día',
  vigilancia_job_atrasado: 'Vigilancia: tarea programada atrasada',
  vigilancia_respaldo_ausente: 'Vigilancia: respaldo ausente',
  vigilancia_pagos_incoherentes: 'Vigilancia: pagos que no cuadran con su venta',
  vigilancia_gastos_incoherentes: 'Vigilancia: impuestos de gasto que no cuadran',
  vigilancia_cierre_desactualizado: 'Vigilancia: el cierre de un día ya no cuadra con sus ventas',
  negocio_actualizado: 'Datos del negocio actualizados',
  plataforma_entro_sandbox: 'Plataforma: entró a un entorno de prueba',
  plataforma_cambio_estado: 'Plataforma: cambió el estado de un negocio',
  plataforma_analista_alta: 'Plataforma: dio acceso a una persona',
  plataforma_permisos_cambiados: 'Plataforma: cambió permisos',
  plataforma_analista_activacion: 'Plataforma: activó o desactivó a una persona',
  plataforma_catalogo_editado: 'Plataforma: editó un catálogo',
  plataforma_consulto_operaciones: 'Plataforma: consultó operaciones de un negocio',
  plataforma_cambio_perfil: 'Plataforma: actualizó su perfil',
};

export function Auditoria() {
  const [eventos, setEventos] = useState<EventoAuditoria[] | null>(null);
  const [tipos, setTipos] = useState<{ tipo: string; veces: number }[]>([]);
  const [tipo, setTipo] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(0);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    auditoriaPlataforma({
      tipos: tipo ? [tipo] : null,
      desde: desde || null,
      hasta: hasta || null,
      limite: POR_PAGINA,
      desplazamiento: pagina * POR_PAGINA,
    })
      .then(setEventos)
      .catch((e) => {
        setEventos([]);
        setError(e instanceof Error ? e.message : 'No pudimos leer la auditoría.');
      });
  }, [tipo, desde, hasta, pagina]);
  useEffect(cargar, [cargar]);
  useEffect(() => {
    tiposEventoPlataforma().then(setTipos).catch(() => setTipos([]));
  }, []);

  const total = eventos?.[0]?.total ?? 0;
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  return (
    <Tarjeta>
      <TextoAyuda>
        Todo lo que pasó en la plataforma y en los negocios, incluidas las acciones de este panel. El registro no se
        edita ni se borra desde aquí.
      </TextoAyuda>

      <div className="mb-3 grid gap-2 sm:grid-cols-4">
        <CampoLista
          etiqueta="Tipo"
          value={tipo}
          onChange={(e) => {
            setTipo(e.target.value);
            setPagina(0);
          }}
          data-e2e="auditoria-tipo"
        >
          <option value="">Todos los tipos</option>
          {tipos.map((t) => (
            <option key={t.tipo} value={t.tipo}>
              {(ETIQUETAS[t.tipo] ?? t.tipo) + ` (${t.veces})`}
            </option>
          ))}
        </CampoLista>
        <Campo
          etiqueta="Desde"
          type="date"
          value={desde}
          onChange={(e) => {
            setDesde(e.target.value);
            setPagina(0);
          }}
        />
        <Campo
          etiqueta="Hasta"
          type="date"
          value={hasta}
          onChange={(e) => {
            setHasta(e.target.value);
            setPagina(0);
          }}
        />
        <div className="flex items-end">
          <p className="text-muted num text-contenido">{total} registros</p>
        </div>
      </div>

      <MensajeError>{error}</MensajeError>

      {eventos === null ? (
        <Esqueleto lineas={8} />
      ) : (
        <TablaAncha>
            <EncabezadoTabla>
                <th className="py-2 pr-2">Cuándo</th>
                <th className="py-2 pr-2">Qué pasó</th>
                <th className="py-2 pr-2">Negocio</th>
                <th className="py-2 pr-2">Quién</th>
                <th className="py-2 pr-2">Origen</th>
                <th className="py-2 pr-2">Detalle</th>
            </EncabezadoTabla>
            <tbody>
              {eventos.map((e) => (
                <FilaTabla key={e.id} className="align-top" data-e2e="fila-auditoria">
                  <td className="num py-2 pr-2 text-etiqueta whitespace-nowrap">{fechaHoraNegocio(e.creadoEn)}</td>
                  <td className="py-2 pr-2 text-contenido font-semibold">{ETIQUETAS[e.tipo] ?? e.tipo}</td>
                  <td className="py-2 pr-2 text-contenido">{e.negocio ?? '—'}</td>
                  <td className="py-2 pr-2 text-contenido">{e.actor ?? '—'}</td>
                  <td className="py-2 pr-2">
                    <Insignia tono={e.creadoVia === 'plataforma' ? 'info' : 'neutro'}>{e.creadoVia}</Insignia>
                  </td>
                  <td className="num py-2 pr-2 text-etiqueta break-all" style={{ maxWidth: '28rem' }}>
                    {Object.keys(e.payload).length ? JSON.stringify(e.payload) : '—'}
                  </td>
                </FilaTabla>
              ))}
              {eventos.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted py-4 text-center text-contenido">
                    No hay registros con esos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </TablaAncha>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <Boton variante="contorno" onClick={() => setPagina((p) => Math.max(0, p - 1))} disabled={pagina === 0}>
          Anterior
        </Boton>
        <span className="text-muted text-contenido">
          Página {pagina + 1} de {ultimaPagina + 1}
        </span>
        <Boton
          variante="contorno"
          onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
          disabled={pagina >= ultimaPagina}
        >
          Siguiente
        </Boton>
      </div>
    </Tarjeta>
  );
}
